/**
 * Predefined service catalog — single source of truth for all service metadata.
 *
 * Rules:
 * - `slug` is the stable internal identifier (ASCII, kebab-case)
 * - `icon` is a Lucide icon name (mapped in UI)
 * - `i18nKey` points to `service.<slug>` in i18n translations
 * - `sortOrder` determines display order
 * - `keywords` are Czech search terms for auto-detection
 * - Services are always fetched from DB; this catalog seeds the DB
 *   and provides the icon/sort mapping for UI
 */

export interface CatalogService {
  slug: string;
  /** i18n key: `service.${slug}` */
  i18nKey: string;
  /** Lucide icon name */
  icon: string;
  sortOrder: number;
  /** Czech keywords for engine detection */
  keywords: string[];
}

export const FACADE_SERVICE_SLUG = "fasadnicke-prace";
export const LEGACY_FACADE_SERVICE_SLUG = "fasady";
export const FACADE_SERVICE_LABEL = "Fasádnické práce";

export const SERVICE_CATALOG: CatalogService[] = [
  {
    slug: "priprava-stavby",
    i18nKey: "service.priprava-stavby",
    icon: "HardHat",
    sortOrder: 1,
    keywords: ["příprava stavby", "staveniště", "výkopy", "zemní práce", "základy"],
  },
  {
    slug: "demontaze-bouraci-prace",
    i18nKey: "service.demontaze-bouraci-prace",
    icon: "Hammer",
    sortOrder: 2,
    keywords: ["demontáž", "bourání", "bourací", "demolice", "odstranění"],
  },
  {
    slug: "zakladaci-prace",
    i18nKey: "service.zakladaci-prace",
    icon: "Layers",
    sortOrder: 3,
    keywords: ["základy", "zakládání", "deska", "základová", "beton"],
  },
  {
    slug: "tesarske-prace",
    i18nKey: "service.tesarske-prace",
    icon: "Axe",
    sortOrder: 4,
    keywords: ["tesař", "krov", "dřevo", "trám", "vazník", "pergola"],
  },
  {
    slug: "pokryvacske-prace",
    i18nKey: "service.pokryvacske-prace",
    icon: "Home",
    sortOrder: 5,
    keywords: ["pokrývač", "střecha", "krytina", "taška", "šindel", "plech", "střechy", "zastřešení"],
  },
  {
    slug: "klempirske-prace",
    i18nKey: "service.klempirske-prace",
    icon: "Wrench",
    sortOrder: 6,
    keywords: ["klempíř", "okapový", "svod", "oplechování", "žlab", "klempířské"],
  },
  {
    slug: "zednicke-prace",
    i18nKey: "service.zednicke-prace",
    icon: "BrickWall",
    sortOrder: 7,
    keywords: ["zedník", "zdění", "omítky", "zdivo", "příčky", "obklady", "dlažba", "betonáž"],
  },
  {
    slug: "sdk",
    i18nKey: "service.sdk",
    icon: "LayoutPanelTop",
    sortOrder: 8,
    keywords: ["sádrokarton", "sdk", "příčky", "podhledy", "suchá stavba"],
  },
  {
    slug: "instalaterske-prace",
    i18nKey: "service.instalaterske-prace",
    icon: "Droplets",
    sortOrder: 9,
    keywords: ["instalatér", "vodoinstalace", "kanalizace", "rozvody vody", "odpad"],
  },
  {
    slug: "topenarske-prace",
    i18nKey: "service.topenarske-prace",
    icon: "Flame",
    sortOrder: 10,
    keywords: ["topení", "radiátor", "kotel", "tepelné čerpadlo", "podlahové topení"],
  },
  {
    slug: "plynarske-prace",
    i18nKey: "service.plynarske-prace",
    icon: "Fuel",
    sortOrder: 11,
    keywords: ["plyn", "plynový", "plynařské", "rozvod plynu"],
  },
  {
    slug: "vzduchotechnicke-prace",
    i18nKey: "service.vzduchotechnicke-prace",
    icon: "Wind",
    sortOrder: 12,
    keywords: ["vzduchotechnika", "ventilace", "rekuperace", "klimatizace", "odvětrání"],
  },
  {
    slug: "elektrikarske-prace",
    i18nKey: "service.elektrikarske-prace",
    icon: "Zap",
    sortOrder: 13,
    keywords: ["elektrikář", "elektro", "elektroinstalace", "zásuvka", "rozvaděč", "kabeláž"],
  },
  {
    slug: "kominicke-prace",
    i18nKey: "service.kominicke-prace",
    icon: "CloudFog",
    sortOrder: 14,
    keywords: ["komín", "kominické", "komínový", "vložka", "čištění komínu"],
  },
  {
    slug: "obkladacske-prace",
    i18nKey: "service.obkladacske-prace",
    icon: "Grid3X3",
    sortOrder: 15,
    keywords: ["obklad", "obklady", "dlažba", "obkladač", "kachličky", "mozaika", "spárování"],
  },
  {
    slug: "malirske-prace",
    i18nKey: "service.malirske-prace",
    icon: "Palette",
    sortOrder: 16,
    keywords: ["malíř", "malování", "malba", "nátěr", "stěna", "strop", "barva"],
  },
  {
    slug: "lakyrniske-prace",
    i18nKey: "service.lakyrniske-prace",
    icon: "Paintbrush",
    sortOrder: 17,
    keywords: ["lakýrník", "lakování", "lak", "nástřik", "stříkání"],
  },
  {
    slug: "tapetarske-prace",
    i18nKey: "service.tapetarske-prace",
    icon: "Wallpaper",
    sortOrder: 18,
    keywords: ["tapetář", "tapeta", "tapetování", "výmalba"],
  },
  {
    slug: "podlaharske-prace",
    i18nKey: "service.podlaharske-prace",
    icon: "Footprints",
    sortOrder: 19,
    keywords: ["podlaha", "podlahář", "laminát", "vinyl", "parkety", "nivelace"],
  },
  {
    slug: "truhlarske-prace",
    i18nKey: "service.truhlarske-prace",
    icon: "TreePine",
    sortOrder: 20,
    keywords: ["truhlář", "truhlářské", "nábytek", "kuchyně", "vestavěné skříně"],
  },
  {
    slug: "fasadnicke-prace",
    i18nKey: "service.fasadnicke-prace",
    icon: "Building2",
    sortOrder: 21,
    keywords: ["fasáda", "zateplení", "polystyren", "eps", "minerální vata", "omítka", "etics", "fasádnické"],
  },
  {
    slug: "dlazdicske-prace",
    i18nKey: "service.dlazdicske-prace",
    icon: "SquareStack",
    sortOrder: 22,
    keywords: ["dlažba", "dlaždice", "dlaždičské", "chodník", "zámková dlažba", "terasa"],
  },
  {
    slug: "uklid",
    i18nKey: "service.uklid",
    icon: "Sparkles",
    sortOrder: 23,
    keywords: ["úklid", "čištění", "úklidové", "stavební úklid"],
  },
];

/** Map slug → CatalogService for quick lookups */
export const SERVICE_BY_SLUG: Record<string, CatalogService> = Object.fromEntries(
  SERVICE_CATALOG.map(s => [s.slug, s])
);

/**
 * Legacy slug mapping — old slugs that should resolve to new catalog slugs.
 * Used for backward compatibility with existing supplier data.
 */
export const LEGACY_SLUG_MAP: Record<string, string> = {
  [LEGACY_FACADE_SERVICE_SLUG]: FACADE_SERVICE_SLUG,
  strechy: "pokryvacske-prace",
  malirstvi: "malirske-prace",
  "natery-malby": "malirske-prace",
  podlahy: "podlaharske-prace",
  "lite-podlahy": "podlaharske-prace",
  elektro: "elektrikarske-prace",
  "voda-topeni": "instalaterske-prace",
  "okna-dvere": "truhlarske-prace",
};

/** Resolve a slug to its canonical catalog slug (handles legacy slugs) */
export function resolveSlug(slug: string | null | undefined): string {
  if (!slug) return "";
  return LEGACY_SLUG_MAP[slug] || slug;
}

export function isFacadeServiceSlug(slug: string | null | undefined): boolean {
  return !!slug && resolveSlug(slug) === FACADE_SERVICE_SLUG;
}

export function normalizeServiceSlugs(slugs: string[] | null | undefined): string[] {
  return Array.from(new Set((slugs || []).map(resolveSlug)));
}

export function includesServiceSlug(
  slugs: string[] | null | undefined,
  targetSlug: string | null | undefined
): boolean {
  if (!targetSlug) return false;
  const canonicalTarget = resolveSlug(targetSlug);
  return normalizeServiceSlugs(slugs).includes(canonicalTarget);
}

/** Check if a slug is a known catalog service (including legacy) */
export function isCatalogService(slug: string): boolean {
  return slug in SERVICE_BY_SLUG || slug in LEGACY_SLUG_MAP;
}
